import {asText} from '@prismicio/helpers'
import React from 'react'

import {useGlobalStrings} from '@/providers/global-strings'
import {ConsentTypes, ReversedConsentTypes} from '@/types/consents'
import getEnvironmentField from '@/utils/get-environment-field'

import type {
    UseConsentsLogicProps,
    UseConsentsLogicReturn,
    ConsentsState,
    ConsentsPayload,
    MappedConsents,
    OnConsentChange,
    CommunicationConsent,
    ImperativeConsent,
    Consent,
} from '@/types/consents'

export const useConsentsLogic = ({
    communicationConsents,
    type,
    imperativeConsents = [],
    consents,
}: UseConsentsLogicProps): UseConsentsLogicReturn => {
    const globalStrings = useGlobalStrings()

    const isExplicit = type === ConsentTypes['Process - explicit']
    const [error, setError] = React.useState<string>()
    const [consentsState, setConsentsState] = React.useState<ConsentsState>(
        getInitialConsentsState(communicationConsents, type),
    )
    const [consentsPayload, setConsentsPayload] = React.useState<ConsentsPayload>()
    const mappedConsents: MappedConsents = getMappedConsents(
        consents,
        communicationConsents,
        type,
        imperativeConsents,
    )
    const onConsentChange: OnConsentChange = (consent, value) => {
        setConsentsState((consents) => {
            if (consents[consent].error) {
                setError(undefined)
            }

            return {
                ...consents,
                [consent]: {...consents[consent], value, error: false},
            }
        })
    }

    React.useEffect(() => {
        setConsentsPayload(
            type !== ConsentTypes.None
                ? getConsentsPayload(mappedConsents, consentsState)
                : undefined,
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consentsState])

    const validateConsents = () => {
        let isValid = true
        setError(undefined)

        if (!consentsState.process.value) {
            setConsentsState((consents) => ({
                ...consents,
                process: {...consents.process, error: true},
            }))

            isValid = false
            setError(globalStrings.consents_error)
        }

        mappedConsents.communication.forEach(({id, required}) => {
            if (required && !consentsState[id].value) {
                setConsentsState((consents) => ({
                    ...consents,
                    [id]: {...consents[id], error: true},
                }))

                isValid = false
                setError(globalStrings.consents_error)
            }
        })

        return isValid
    }

    return {
        consentsProps: {
            consents: mappedConsents,
            onConsentChange,
            consentsState,
            isExplicit,
            error,
            type,
        },
        ConsentTypes,
        consentsPayload,
        validateConsents,
    }
}

const getMappedConsents = (
    consents: Consent[],
    communicationConsents: CommunicationConsent[],
    type: ConsentTypes,
    imperativeConsents: ImperativeConsent[],
) => {
    const temp = imperativeConsents.map(({consent}) => ({...consent.document?.data}))
    const imperativeProcessConsent = temp.find((consent) => consent.type === type)
    const imperativeCommunicationConsents = temp.filter(
        ({type}) => type === ReversedConsentTypes.communication,
    )

    return {
        process:
            imperativeProcessConsent?.text.richText ||
            consents.find(
                (consent) => consent.type === ReversedConsentTypes[type] && consent.isDefault,
            )?.text.richText,
        communication: getMappedCommunicationConsents(
            consents,
            communicationConsents,
            type,
            imperativeCommunicationConsents,
        ),
    }
}

const getConsentsPayload = (consents: MappedConsents, state: ConsentsState): ConsentsPayload => ({
    consentToProcess: state.process.value,
    text: asText(consents.process) || '',
    communications: consents.communication.map(({id, label}) => ({
        value: state[id].value,
        subscriptionTypeId: id,
        text: asText(label),
    })),
})

const getInitialConsentsState = (
    communicationConsents: CommunicationConsent[],
    type: ConsentTypes,
) => {
    let initialConsentsState: ConsentsState = {
        process: {
            error: false,
            value: [
                ConsentTypes['Legitimate interest'],
                ConsentTypes['Process - implicit'],
            ].includes(type),
        },
    }

    communicationConsents.forEach(({id}) => {
        initialConsentsState[id] = {error: false, value: false}
    })

    return initialConsentsState
}

const getMappedCommunicationConsents = (
    consents: Consent[],
    communicationConsents: CommunicationConsent[],
    type: ConsentTypes,
    imperativeConsents: Consent[],
) => {
    if (communicationConsents.length < 1 || type === ConsentTypes['Legitimate interest']) {
        return []
    }

    const prismicCommunicationConsents = consents
        .filter(({type}) => type === ReversedConsentTypes.communication)
        .map(({text, subscriptionTypeStaging, subscriptionTypeProduction, isDefault}) => ({
            isDefault,
            text: text.richText,
            id: getEnvironmentField({
                production: subscriptionTypeProduction?.subscriptionId,
                staging: subscriptionTypeStaging?.subscriptionId,
            }),
        }))

    return communicationConsents.map(({id, required}) => {
        const label =
            imperativeConsents.find(({subscriptionTypeProduction, subscriptionTypeStaging}) =>
                [
                    subscriptionTypeProduction?.subscriptionId,
                    subscriptionTypeStaging?.subscriptionId,
                ].includes(id),
            )?.text.richText ||
            prismicCommunicationConsents.find((consent) => consent.id === id && consent.isDefault)
                ?.text

        return {id, label, required}
    })
}
